import { convertDistance, deepEqual, getMillisecondsFromTimeDelta, getFullTimeFromTimeDelta, areAllObjectValuesEmpty, formattedAddress as getFormattedAddress } from '#helper';

/**
 * Provides methods to deeply
 * clean array or object from null and undefined.
 */

export class Cleaner {
  /**
   * Cleans array items and nested elements from null and undefined.
   * @param {Array} arr array to be cleaned.
   * @returns {Array} new cleaned array.
   */

  static cleanArray(arr) {
    return arr.reduce((acc, item) => {
      if (Array.isArray(item)) {
        const newItem = this.cleanArray(item);
        if (newItem.length > 0) {
          acc.push(newItem);
        }
      } else if (item instanceof Object && !(item instanceof Date)) {
        const newItem = this.cleanObject(item);
        if (Object.keys(newItem).length > 0) {
          acc.push(newItem);
        }
      } else if (item !== null && item !== undefined) {
        acc.push(item);
      }

      return acc;
    }, []);
  }

  /**
   * Cleans object properties and nested elements from null and undefined.
   * @param {Object} obj object to be cleaned
   * @returns {Object} new cleaned object
   */

  static cleanObject(obj) {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (Array.isArray(value)) {
        const newValue = this.cleanArray(value);
        if (newValue.length > 0) {
          acc[key] = newValue;
        }
      } else if (value instanceof Object && !(value instanceof Date)) {
        const newValue = this.cleanObject(value);
        if (Object.keys(newValue).length > 0) {
          acc[key] = newValue;
        }
      } else if (value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});
  }
}

export const isPristineForm = (currentForm, prevFormValues) => {
  const touched = {};
  const isPrevFormValuesExist = prevFormValues && Object.keys(prevFormValues).length !== 0;

  if (isPrevFormValuesExist && deepEqual(currentForm, prevFormValues)) {
    return true;
  }

  ['company', 'address', 'formattedAddress', 'locations'].forEach(field => {
    if (field === 'locations' && currentForm[field]) {
      touched[field] = currentForm[field]?.length > 0 || '';
    }
    touched[field] = (currentForm[field] &&
      Object.values(currentForm[field]).some(v => v && v.length)) || '';
  });

  return !Object.values(touched).some(v => v);
};

export const omitValues = source => {
  if (!source) {
    return {};
  }
  const { addressbookContactId, id, date, minutes, done, timedelta, ...rest } = source;

  return rest;
};

export const valuesToAddressbookFormat = (values, measurementSystem) => ({
  companyName: values.companyName || null,
  firstName: values.firstName || null,
  lastName: values.lastName || null,
  addressbookId: values.addressbook?.id || null,
  locations: values.locations?.length ?
    [{
      ...values.locations[0],
      radius: convertDistance(Number(values.radius), measurementSystem, 'metric')
    }] : undefined,
  address1: values.address?.address1 || null,
  address2: values.address?.address2 || null,
  city: values.address?.city || null,
  state: values.address?.state || null,
  country: values.address?.country || null,
  zipCode: values.address?.zipCode || null
});

export const mapDeliverySubmitValues = delivery => delivery.map(({ company, ...state }) => {
  const res = { ...state };
  if (company) {
    res.company = { ...company };
    if (Object.prototype.hasOwnProperty.call(res.company, 'companyName')) {
      res.company.label = res.company.companyName;
      delete res.company.companyName;
    }
  }
  return res;
});

export const mapDeliveryInitialValues = (delivery, measurementSystem) =>
  delivery.map(({ company, ...state }) => {
    const res = { ...state };
    if (company || state.address) {
      res.company = {
        companyName: company?.label ?? null,
        firstName: company?.firstName ?? null,
        lastName: company?.lastName ?? null
      };
    }
    if (res.address && !res.formattedAddress) {
      res.formattedAddress = getFormattedAddress(res.address);
    }
    if (res.timedelta) {
      res.minutes = getFullTimeFromTimeDelta(res.timedelta);
    }
    res.radius = res.locations?.[0]?.radius ?
      convertDistance(res.locations?.[0]?.radius, 'metric', measurementSystem) : null;
    return res;
  });

export const isStateDone = state =>
  state.date && !state.locations && new Date(state.date) < new Date();

export const formatDeliveryValues = (states, measurementSystem, delivery) => {
  const newStates = states.map((state, index) => {
    let newState = { ...state };
    if (delivery) {
      newState = {
        ...state,
        addressbookContactId: delivery?.[index]?.addressbookContactId
      };
    }
    if (isStateDone(state)) {
      newState.done = new Date();
    }
    if (Object.prototype.hasOwnProperty.call(state, 'done') && !isStateDone(state)) {
      delete newState.done;
    }
    if (state.minutes && state.date && state.locations) {
      const timedeltaValue = !areAllObjectValuesEmpty(state.minutes) ?
        getMillisecondsFromTimeDelta(state.minutes) : undefined;
      newState.timedelta = timedeltaValue;
    }
    if (state.radius && state.locations) {
      newState.locations = [{ ...state.locations[0], radius: convertDistance(state.radius, measurementSystem, 'metric') }];
    }
    if (newState.timedelta && !newState.locations) {
      delete newState.timedelta;
    }
    delete newState.minutes;
    delete newState.radius;
    if (typeof newState.id === 'string') {
      delete newState.id;
    }
    return newState;
  });

  return Cleaner.cleanArray(mapDeliverySubmitValues(newStates))
    .map(({ formattedAddress, ...rest }) => ({ ...rest }));
};

export const withAddressbookContactId = (delivery, deliveryFormIndex, contactId) =>
  delivery.map((d, i) =>
    (i === deliveryFormIndex ? ({ ...d, addressbookContactId: contactId }) : d));

const normalizeSelectOptions = options =>
  (Array.isArray(options) ? options.map(option => (option != null ? String(option) : option)) : []);

// Legacy shipments stored the option index instead of the option text
const isOptionIndex = (value, options) =>
  (typeof value === 'number' || /^\d+$/.test(value)) &&
  !options.includes(String(value)) &&
  options[Number(value)] !== undefined;

export const resolveCustomFieldValue = (value, options) => {
  if (value == null || value === '') {
    return undefined;
  }
  return isOptionIndex(value, options) ? options[Number(value)] : String(value);
};

export const customFieldsWithSelectOptions = (shipmentCustomFields = [], projectCustomFields) =>
  shipmentCustomFields.map(shipmentField => {
    const projectField = projectCustomFields?.find(pf => pf.key === shipmentField.key);
    let selectOptions = normalizeSelectOptions(projectField?.value);
    const fieldValue = resolveCustomFieldValue(shipmentField.value, selectOptions);

    const isUnknownOption = fieldValue !== undefined && selectOptions.length > 0 &&
      !selectOptions.includes(fieldValue);
    if (isUnknownOption) {
      selectOptions = [fieldValue, ...selectOptions];
    }

    return {
      ...(selectOptions.length > 0 ? { selectOptions } : {}),
      ...shipmentField,
      ...(fieldValue !== undefined ? { value: fieldValue } : {})
    };
  });

export const formatCustomField = customFields =>
  customFields.map(customField => {
    const { value, label, key } = customField;

    const options = {
      label,
      key
    };

    if (!value) {
      return options;
    }

    const stringValues = Array.isArray(value) ?
      value.map(v => (v != null ? String(v) : v)) :
      [String(value)];

    const [singleValue] = stringValues;

    if (stringValues.length === 1) {
      options.value = singleValue;
    } else {
      options.selectOptions = stringValues;
    }

    return options;
  });
